import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Btn, Badge, AInput, ATextarea, ASelect, EmptyState } from "../AdminUI";
import { Star, Trash2, Check, X, Archive, Upload, Link2, Send, Copy, Plus, ShieldCheck } from "lucide-react";
import { StarRow, StarPicker } from "@/components/site/StarPicker";

const STATUS_COLOR = { pending: "amber", approved: "green", rejected: "red", archived: "gray" };
const SOURCE_OPTS = [
  { value: "imported_google", label: "Imported Google" },
  { value: "internal", label: "Internal" },
  { value: "admin_import", label: "Admin Import" },
  { value: "other_legacy", label: "Other Legacy Source" },
];

function ImportModal({ onClose, onSaved }) {
  const blank = { reviewer_name: "", rating: 5, review_text: "", review_date: "", service_type: "", source: "imported_google", source_label: "Originally posted on Google", source_url: "", verified_customer: false };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async (again) => {
    if (!f.reviewer_name || !f.review_text) { toast.error("Name and review text are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/reviews", f);
      toast.success("Review imported");
      onSaved();
      if (again) setF({ ...blank, source: f.source, source_label: f.source_label }); else onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="import-review-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b"><h3 className="font-head font-bold text-lg text-[var(--navy)] uppercase">Import Review</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AInput label="Reviewer Name" value={f.reviewer_name} onChange={(e) => upd("reviewer_name", e.target.value)} data-testid="import-name" />
            <div><label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Rating</label><StarPicker value={f.rating} onChange={(v) => upd("rating", v)} size="w-7 h-7" /></div>
            <AInput label="Original Date (optional)" value={f.review_date} onChange={(e) => upd("review_date", e.target.value)} placeholder="e.g. 2024-08-15" />
            <AInput label="Service (optional)" value={f.service_type} onChange={(e) => upd("service_type", e.target.value)} />
            <ASelect label="Source" value={f.source} options={SOURCE_OPTS} onChange={(e) => upd("source", e.target.value)} />
            <AInput label="Source Label" value={f.source_label} onChange={(e) => upd("source_label", e.target.value)} />
            <div className="sm:col-span-2"><AInput label="Source URL (optional)" value={f.source_url} onChange={(e) => upd("source_url", e.target.value)} /></div>
            <div className="sm:col-span-2"><ATextarea label="Review Text" rows={5} value={f.review_text} onChange={(e) => upd("review_text", e.target.value)} data-testid="import-text" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={f.verified_customer} onChange={(e) => upd("verified_customer", e.target.checked)} /> Mark as Verified Customer (only if legitimately known)</label>
          <div className="flex flex-wrap gap-2 pt-2">
            <Btn onClick={() => save(false)} loading={busy} data-testid="save-review">Save Review</Btn>
            <Btn variant="ghost" onClick={() => save(true)} loading={busy} data-testid="save-add-another">Save &amp; Add Another</Btn>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ review, onClose, onSaved }) {
  const [f, setF] = useState(review);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/reviews/${f.id}`, { display_name: f.display_name, rating: f.rating, review_text: f.review_text, service_type: f.service_type, source_label: f.source_label, admin_notes: f.admin_notes, verified_customer: f.verified_customer });
      toast.success("Saved"); onSaved(); onClose();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="review-detail-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b"><h3 className="font-head font-bold text-lg text-[var(--navy)] uppercase">Review Detail</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <span className="text-gray-400">Reviewer</span><span className="font-medium">{f.reviewer_name}</span>
            <span className="text-gray-400">Email (private)</span><span className="font-medium">{f.reviewer_email || "—"}</span>
            <span className="text-gray-400">Phone (private)</span><span className="font-medium">{f.reviewer_phone || "—"}</span>
            <span className="text-gray-400">Source</span><span className="font-medium">{f.source} {f.source_label ? `· ${f.source_label}` : ""}</span>
            <span className="text-gray-400">Submitted</span><span className="font-medium">{f.submitted_at}</span>
            <span className="text-gray-400">Linked Customer</span><span className="font-medium">{f.customer_id || "—"}</span>
            <span className="text-gray-400">Linked Job</span><span className="font-medium">{f.job_id || "—"}</span>
            <span className="text-gray-400">Verified</span><span className="font-medium">{f.verified_customer ? "Yes" : "No"}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <AInput label="Public Display Name" value={f.display_name || ""} onChange={(e) => upd("display_name", e.target.value)} />
            <div><label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Rating</label><StarPicker value={f.rating} onChange={(v) => upd("rating", v)} size="w-7 h-7" /></div>
            <AInput label="Service" value={f.service_type || ""} onChange={(e) => upd("service_type", e.target.value)} />
            <AInput label="Source Label" value={f.source_label || ""} onChange={(e) => upd("source_label", e.target.value)} />
          </div>
          <ATextarea label="Review Text" rows={5} value={f.review_text || ""} onChange={(e) => upd("review_text", e.target.value)} />
          <ATextarea label="Admin Notes (private)" rows={2} value={f.admin_notes || ""} onChange={(e) => upd("admin_notes", e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={!!f.verified_customer} onChange={(e) => upd("verified_customer", e.target.checked)} /> Verified Customer</label>
          <div className="flex gap-2"><Btn onClick={save} loading={busy} data-testid="save-detail">Save Changes</Btn><Btn variant="ghost" onClick={onClose}>Close</Btn></div>
        </div>
      </div>
    </div>
  );
}

function RequestsPanel() {
  const [reqs, setReqs] = useState([]);
  const [f, setF] = useState({ reviewer_name: "", email: "", service_type: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/review-requests").then((r) => setReqs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (send) => {
    if (!f.reviewer_name && !f.email) { toast.error("Enter a name or email."); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/review-requests", f);
      if (send && f.email) { await api.post(`/admin/review-requests/${r.data.id}/send`); toast.success("Request sent"); }
      else { await navigator.clipboard?.writeText(r.data.url).catch(() => {}); toast.success("Link created" + (send ? "" : " & copied")); }
      setF({ reviewer_name: "", email: "", service_type: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Copied"); };
  const send = async (id) => { try { const r = await api.post(`/admin/review-requests/${id}/send`); toast[r.data.ok ? "success" : "error"](r.data.ok ? "Sent" : (r.data.error || "Failed")); load(); } catch { toast.error("Failed"); } };
  const base = (process.env.REACT_APP_BACKEND_URL || "");
  return (
    <div className="space-y-4">
      <Card title="New Review Request">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AInput label="Customer Name" value={f.reviewer_name} onChange={(e) => setF({ ...f, reviewer_name: e.target.value })} data-testid="req-name" />
          <AInput label="Email (for sending)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="req-email" />
          <AInput label="Service (optional)" value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} />
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Btn onClick={() => create(false)} loading={busy} data-testid="create-request"><Link2 className="w-4 h-4" /> Create Link</Btn>
          <Btn variant="ghost" onClick={() => create(true)} loading={busy} data-testid="create-send-request"><Send className="w-4 h-4" /> Create &amp; Send Email</Btn>
        </div>
      </Card>
      {reqs.length === 0 ? <Card><EmptyState icon={Link2} title="No review requests yet" /></Card> : (
        <Card title="Review Requests">
          <div className="divide-y">
            {reqs.map((q) => {
              const url = `${base.replace(/\/$/, "")}/review/${q.token}`;
              return (
                <div key={q.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="request-row">
                  <div className="min-w-0"><p className="font-semibold text-sm text-[var(--navy)]">{q.reviewer_name || q.email || "—"}</p>
                    <p className="text-xs text-gray-400 truncate">{q.service_type} · created {q.created_at?.slice(0, 10)}{q.submitted_at ? ` · submitted ${q.submitted_at.slice(0, 10)}` : ""}</p></div>
                  <div className="flex items-center gap-2">
                    <Badge color={q.status === "submitted" ? "green" : q.status === "sent" ? "blue" : q.status === "opened" ? "amber" : "gray"}>{q.status}</Badge>
                    <Btn variant="ghost" onClick={() => copy(url)}><Copy className="w-4 h-4" /></Btn>
                    {q.email && q.status !== "submitted" && <Btn variant="ghost" onClick={() => send(q.id)}><Send className="w-4 h-4" /></Btn>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function ReviewsAdmin() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [sel, setSel] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => {
    if (tab === "requests") return;
    api.get(`/admin/reviews?status=${tab}`).then((r) => { setItems(r.data); setSel([]); }).catch(() => {});
    api.get("/admin/reviews/summary").then((r) => setCounts(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const act = async (id, patch) => { await api.patch(`/admin/reviews/${id}`, patch); load(); };
  const del = async (id) => { if (!window.confirm("Delete this review permanently?")) return; await api.delete(`/admin/reviews/${id}`); load(); };
  const bulk = async (action) => {
    if (sel.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${sel.length} review(s) permanently?`)) return;
    await api.post("/admin/reviews/bulk", { ids: sel, action }); toast.success("Done"); load();
  };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const TABS = [
    ["pending", `Pending (${counts.pending || 0})`], ["approved", `Approved (${counts.approved || 0})`],
    ["rejected", `Rejected (${counts.rejected || 0})`], ["archived", `Archived (${counts.archived || 0})`],
    ["all", "All"], ["requests", "Requests"],
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Reviews</h1>
        <Btn onClick={() => setShowImport(true)} data-testid="import-review-btn"><Upload className="w-4 h-4" /> Import Review</Btn>
      </div>

      <div className="flex gap-2 flex-wrap border-b border-gray-200 pb-2">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-[var(--navy)] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{label}</button>
        ))}
      </div>

      {tab === "requests" ? <RequestsPanel /> : (
        <>
          {sel.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="bulk-bar">
              <span className="text-sm text-blue-800 font-medium">{sel.length} selected</span>
              <Btn variant="ghost" onClick={() => bulk("approve")}><Check className="w-4 h-4" /> Approve</Btn>
              <Btn variant="ghost" onClick={() => bulk("reject")}><X className="w-4 h-4" /> Reject</Btn>
              <Btn variant="ghost" onClick={() => bulk("archive")}><Archive className="w-4 h-4" /> Archive</Btn>
              <Btn variant="danger" onClick={() => bulk("delete")}><Trash2 className="w-4 h-4" /> Delete</Btn>
            </div>
          )}
          {items.length === 0 ? <Card><EmptyState icon={Star} title="No reviews here" /></Card> : (
            <div className="space-y-3">
              {items.map((r) => (
                <Card key={r.id} className="!p-0">
                  <div className="p-4 flex gap-3" data-testid="admin-review-row">
                    <input type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="mt-1.5 shrink-0" data-testid={`select-${r.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StarRow n={r.rating || 5} />
                        <span className="font-semibold text-[var(--navy)]">{r.display_name || r.reviewer_name}</span>
                        <Badge color={STATUS_COLOR[r.status] || "gray"}>{r.status}</Badge>
                        {r.featured && <Badge color="navy">Featured</Badge>}
                        {r.verified_customer && <span className="inline-flex items-center gap-1 text-[11px] text-[var(--action)]"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                        {r.source === "imported_google" && <Badge color="amber">Google (imported)</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">"{r.review_text}"</p>
                      <p className="text-xs text-gray-400 mt-1">{r.service_type ? `${r.service_type} · ` : ""}{(r.review_date || r.submitted_at || "").slice(0, 10)}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        <Btn variant="ghost" onClick={() => setDetail(r)} data-testid={`open-${r.id}`}>Open</Btn>
                        {r.status !== "approved" && <Btn variant="ghost" onClick={() => act(r.id, { status: "approved" })}><Check className="w-4 h-4" /> Approve</Btn>}
                        {r.status !== "rejected" && <Btn variant="ghost" onClick={() => act(r.id, { status: "rejected" })}><X className="w-4 h-4" /> Reject</Btn>}
                        {r.status !== "archived" && <Btn variant="ghost" onClick={() => act(r.id, { status: "archived" })}><Archive className="w-4 h-4" /> Archive</Btn>}
                        <Btn variant="ghost" onClick={() => act(r.id, { featured: !r.featured })}><Star className="w-4 h-4" /> {r.featured ? "Unfeature" : "Feature"}</Btn>
                        <Btn variant="danger" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Btn>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {showImport && <ImportModal onClose={() => setShowImport(false)} onSaved={load} />}
      {detail && <DetailModal review={detail} onClose={() => setDetail(null)} onSaved={load} />}
    </div>
  );
}
